import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TextField, Select, Note } from "@appsflyer/fe-ui-core";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useLocation } from "react-router-dom";
import { amplitudeTrack_inputChange, InputType } from "../services/utils";

type AdRevenueParamFormProps = {
  parameters: IAdRevenueParam[];
  onParamChange: (params: IAdRevenueParam[]) => void;
  availablePredefinedOptions: IOption[];
};

export type IOption = {
  value: string;
  label: string;
};

export type IAdRevenueParam = {
  type: "predefined" | "custom";
  value: string;
};

const AdRevenueParamRow: React.FC<{
  param: IAdRevenueParam;
  index: number;
  handleTypeChange: (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  handleValueChange: (index: number, value: string) => void;
  handleRemoveParameter: (index: number) => void;
  availablePredefinedOptions: IOption[];
  maxPredefinedReached: boolean;
  alreadySelected: string[];
}> = ({
  param,
  index,
  handleTypeChange,
  handleValueChange,
  handleRemoveParameter,
  availablePredefinedOptions,
  maxPredefinedReached,
  alreadySelected,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      mt={2}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        borderRadius: "8px",
        transition: "background 0.2s ease-in-out",
        "&:hover": {},
      }}
    >
      <RadioGroup
        row
        value={param.type}
        onChange={(event) => handleTypeChange(index, event)}
      >
        <FormControlLabel
          control={<Radio />}
          label={t("adRevenue_predefined")}
          value="predefined"
          disabled={maxPredefinedReached && param.type !== "predefined"}
          sx={{ mt: "-8px" }}
        />
        <FormControlLabel
          control={<Radio />}
          label={t("adRevenue_custom")}
          value="custom"
          sx={{ mt: "-8px" }}
        />
      </RadioGroup>

      {param.type === "predefined" ? (
        <Select
          id={`predefined-param-${index}`}
          variant="outlined"
          size="medium"
          placeholder={
            param.value ? param.value : t("adRevenue_choose_parameter")
          }
          value={
            availablePredefinedOptions.find(
              (opt) => opt.value.toLowerCase() === param.value.toLowerCase()
            ) ?? ""
          }
          onChange={(e) => handleValueChange(index, e?.value ?? "")}
          options={availablePredefinedOptions.filter(
            (opt) =>
              opt.value.toLowerCase() === param.value.toLowerCase() ||
            !alreadySelected.some(
              (used) => used.toLowerCase() === opt.value.toLowerCase()
            )          )}
          
        />
      ) : (
        <TextField
          variant="outlined"
          id={`custom-param-${index}`}
          placeholder={t("adRevenue_custom_parameter")}
          value={param.value}
          onChange={(e) => handleValueChange(index, e.target.value)}
        />
      )}

      {hovered && (
        <IconButton onClick={() => handleRemoveParameter(index)}>
          <DeleteOutlineOutlinedIcon color="error" />
        </IconButton>
      )}
    </Box>
  );
};

const AdRevenueParamForm: React.FC<AdRevenueParamFormProps> = ({
  parameters,
  onParamChange,
  availablePredefinedOptions,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, []);

  const predefinedCount = parameters.filter(
    (param) => param.type === "predefined"
  ).length;
  const maxPredefinedReached = predefinedCount >= 4;

  const handleAddParameter = () => {
    const newParam: IAdRevenueParam = maxPredefinedReached
      ? { type: "custom", value: "" }
      : { type: "predefined", value: "" };
    onParamChange([...parameters, newParam]);
  };

  const handleRemoveParameter = (index: number) => {
    const updatedParams = [...parameters];
    updatedParams.splice(index, 1);
    onParamChange(updatedParams);
  };

  const handleTypeChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const updatedParams = [...parameters];
    updatedParams[index].type = event.target.value as "predefined" | "custom";

    if (event.target.value === "predefined") {
      updatedParams[index].value = "";
    }

    amplitudeTrack_inputChange(
      location.state,
      InputType.Radio,
      `parameter_type_${index}`,
      event.target.value
    );
  
    onParamChange(updatedParams);
  };

  const handleValueChange = (index: number, value: string) => {
    const updatedParams = [...parameters];
    updatedParams[index].value = value ?? "<<CUSTOM_PARAM_KEY>>";

    amplitudeTrack_inputChange(
      location.state,
      updatedParams[index].type === "predefined" ? InputType.Select : InputType.TextField,
      `parameter_value_${index}`,
      value
    );
    
    onParamChange(updatedParams);
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ maxHeight: 320, overflowY: "auto", mt: -1 }}
      >
        {parameters.length === 0 && (
          <Note title={t("adRevenue_no_parameters")} sx={{ mt: 2 }} />
        )}
        {(() => {
          const selectedPredefinedValues = parameters
            .filter((param) => param.type === "predefined")
            .map((param) => param.value);

          return parameters.map((param, index) => (
            <AdRevenueParamRow
              key={index}
              param={param}
              index={index}
              handleTypeChange={handleTypeChange}
              handleValueChange={handleValueChange}
              handleRemoveParameter={handleRemoveParameter}
              availablePredefinedOptions={availablePredefinedOptions}
              maxPredefinedReached={maxPredefinedReached}
              alreadySelected={selectedPredefinedValues}
            />
          ));
        })()}
      </Box>
      <Button
        onClick={handleAddParameter}
        // fullWidth
        variant="outlined"
        size="large"
        sx={{ mt: 2, borderRadius: "100px" }}
        startIcon={<AddRoundedIcon />}
      >
        {t("adRevenue_add_parameter")}
      </Button>
    </>
  );
};

export default AdRevenueParamForm;
